import concat from 'lodash/concat';
import forEach from 'lodash/forEach';
import isNil from 'lodash/isNil';
import { createPaymentRequest, abortPaymentRequest, isSupportApplePay } from './w3cCheckoutPayment';
import { appleAddressParse } from './addressParse';
import { emailParse } from './emailParse';
import { objectFilter } from '../../../../../../shared/utils/commonUtils';

const DISPALYITEMSMAP = {
  subTotal: 'checkout.lib.components.orderDetailContent.subTotal',
  totalAmount: 'checkout.lib.components.text.orderTotal',
  processingFee: 'checkout.lib.components.text.serviceFee',
  paymentProcessingFee: 'checkout.lib.components.text.totalPPF',
  aaDiscount: 'checkout.lib.components.text.aaDiscount',
};

const APPLEPAY_METHOD = 'https://apple.com/apple-pay';
const MERCHANT_IDENTIFIER = 'merchant.com.active.mstest';

class ApplePayment {
  sessionId = undefined;

  paymentRequest = undefined;

  buildPaymentMethodData = (initParams) => {
    const {
      collectBillingInfo, paymentCountryCode, applePayInfo, orderInfo: { currencyCode },
    } = initParams;
    const { version, supportCardTypes, merchantCapabilities } = applePayInfo;
    return [{
      supportedMethods: APPLEPAY_METHOD,
      data: {
        version,
        merchantIdentifier: MERCHANT_IDENTIFIER,
        merchantCapabilities,
        supportedNetworks: supportCardTypes,
        currencyCode,
        countryCode: paymentCountryCode,
        requiredBillingContactFields: collectBillingInfo ? [ 'postalAddress', 'name', 'email' ] : [],
        requiredShippingContactFields: collectBillingInfo ? [ 'email' ] : [],
      },
    }];
  };

  validateMerchant = initParams => (event) => {
    const { getApplePaySession, commitCallback } = initParams;
    const params = {
      domainName: window.location.host,
      displayName: 'Active Network',
      startSessionUrl: event.validationURL,
    };
    getApplePaySession({ params }).unwrap().then(({ sessionId, applePaySession }) => {
      this.sessionId = sessionId;
      event.complete(JSON.parse(applePaySession));
    }).catch(() => {
      abortPaymentRequest(this.paymentRequest);
      commitCallback({ status: false });
    });
  };

  handleCreateApplePayAccount = ({
    details, initParams, billingContact, receiptEmail,
  }) => {
    const {
      i18n, createApplePayAccount, commitCallback, logTrack,
    } = initParams;
    const params = {
      SessionID: this.sessionId,
      ApplePayPaymentData: JSON.stringify(details),
    };
    createApplePayAccount({ params, collectHeaderToken: false }).unwrap().then((rsp) => {
      if (rsp === 'SUCCESS') {
        const data = {
          status: true, paymentAccountId: this.sessionId, paymentMethodType: 'APPLEPAY', billingContact, receiptEmail,
        };
        commitCallback(objectFilter(data));
      } else {
        const msg = i18n('checkout.lib.errorCode.applePay');
        commitCallback({ status: false, msg });
        logTrack?.({
          params: {
            message: `create apple pay acount failed: ${msg}`,
          },
        });
      }
    }).catch(() => {
      commitCallback({ status: false });
    });
  };

   buildShoppingCartDetails = (initParams) => {
     const {
       i18n, showDetailInfo, orderInfo, yourPayment,
     } = initParams;
     const {
       totalAmount, subTotal, processingFee, paymentProcessingFee,
       aaDiscount, currencyCode, surchargeGroup = [],
     } = orderInfo;
     const displayItem = {
       subTotal,
       surchargeGroup,
       processingFee,
       aaDiscount,
       paymentProcessingFee,
     };
     if (!showDetailInfo) {
       return {
         total: {
           label: i18n('checkout.lib.components.payment.applePay.paymentSheet.totalLabel'),
           amount: {
             value: isNil(yourPayment) ? totalAmount.toString() : yourPayment.toString(),
             currency: currencyCode,
           },
         },
       };
     }
     const items = objectFilter(displayItem);
     let displayItems = [];
     forEach(items, (v, k) => {
       if (v instanceof Array) {
         displayItems = concat(displayItems, v.map(item => ({
           label: item.name,
           amount: {
             value: item.amount.toString(),
             currency: currencyCode,
           },
         })));
       } else {
         displayItems = concat(displayItems, [{
           label: i18n(DISPALYITEMSMAP[k]),
           amount: {
             value: v.toString(),
             currency: currencyCode,
           },
         }]);
       }
     });
     return {
       displayItems,
       total: {
         label: i18n('checkout.lib.components.text.orderTotal'),
         amount: {
           value: isNil(yourPayment) ? totalAmount.toString() : yourPayment.toString(),
           currency: currencyCode,
         },
       },
     };
   };

  startApplePayProcess = (initParams) => {
    const {
      i18n, commitCallback, logTrack, enablePreviewMode,
    } = initParams;
    const paymentData = this.buildPaymentMethodData(initParams);
    const cartDetails = this.buildShoppingCartDetails(initParams);
    this.paymentRequest = createPaymentRequest(paymentData, cartDetails, null);
    this.paymentRequest.onmerchantvalidation = this.validateMerchant(initParams);
    this.paymentRequest.show().then((result) => {
      const { details } = result;
      result.complete('success');
      if (enablePreviewMode) {
        commitCallback({ status: true });
        return;
      }
      const { billingContact = {}, shippingContact = {} } = details;
      const formattedBillingAddress = appleAddressParse(billingContact);
      const email = emailParse(shippingContact.emailAddress);
      this.handleCreateApplePayAccount({
        details, initParams, billingContact: formattedBillingAddress, receiptEmail: email,
      });
    }).catch((error) => {
      abortPaymentRequest(this.paymentRequest);
      commitCallback({ status: false, msg: i18n('checkout.lib.errorCode.applePay') });
      logTrack?.({
        params: {
          message: `create apple pay acount failed: ${error}`,
        },
      });
    });
  };
}

export const applePayment = new ApplePayment();

export const checkApplePaySupport = (initParams) => {
  const { paymentCountryCode, applePayInfo } = initParams;
  const frontSupportApplePay = !!isSupportApplePay(paymentCountryCode);
  return frontSupportApplePay && !!applePayInfo;
};
